import { Component, ElementRef, Input, OnChanges, OnInit } from '@angular/core';
import {
  isOptionActive,
  isResCustomer,
  Option,
  sortThresholdValuesFromBackend,
  sortThresholdValuesFromView,
  Threshold,
} from '@dch/products-business-logic';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  CustomerService,
  InceptionService,
  TranslationService,
} from '@proximus/ng-common';
import {
  ApplicationState,
  getAlarmThresholdActivated,
  getAlarmThresholdUpdated,
  getNewAlarmThresholds,
} from '../../../store/reducers';
import { Store } from '@ngrx/store';
import {
  UpdateThresholds,
  UpdateThresholdsReset,
} from '../../../store/actions/alarm-threshold.actions';
import { OnDestroyHookDirective } from '../../../shared/hooks';
import { Alert, AlertsService } from '@proximus/ng-common-components';
import { combineLatest } from 'rxjs';

export const formMatcher = (
  control: AbstractControl,
): { [key: string]: boolean } => {
  const notif1 = control.value.notification1;
  const notif2 = control.value.notification2;
  if (!notif1 && !notif2) {
    return { atLeastOne: true };
  }
  if (notif1 === notif2) {
    return { mustBeDifferent: true };
  }
  return null;
};

@Component({
  selector: 'mprd-alarm-threshold',
  templateUrl: './alarm-threshold.component.html',
})
export class AlarmThresholdComponent
  extends OnDestroyHookDirective
  implements OnInit, OnChanges
{
  @Input()
  public option: Option;
  @Input()
  public msisdn: string;
  public showForm: boolean;
  public updateThresholdsForm: UntypedFormGroup;
  public isRes: boolean;

  constructor(
    private customerService: CustomerService,
    private store: Store<ApplicationState>,
    private alertService: AlertsService,
    private translationService: TranslationService,
    private inceptionService: InceptionService,
    private elementRef: ElementRef,
  ) {
    super();
  }

  ngOnInit() {
    this.inceptionServiceInit();
  }

  ngOnChanges() {
    this.addSubscription(
      this.customerService.getCustomerAccount().subscribe((customerAccount) => {
        this.isRes = isResCustomer(customerAccount);
        if (this.option) {
          this.showForm = isOptionActive(this.option);
          const threshold = sortThresholdValuesFromBackend(
            this.option.threshold || { lowThreshold: 10, highThreshold: 50 },
          );
          this.updateThresholdsForm = new UntypedFormGroup(
            {
              notification1: new UntypedFormControl(
                threshold.lowThreshold === -1 ? '' : threshold.lowThreshold,
              ),
              notification2: new UntypedFormControl(
                threshold.highThreshold === -1 ? '' : threshold.highThreshold,
              ),
            },
            formMatcher,
          );
          if (this.isRes) {
            this.updateThresholdsForm.controls['notification1'].setValidators([
              Validators.min(1),
              Validators.max(999),
              Validators.pattern('^-?[0-9]+$'),
            ]);
            this.updateThresholdsForm.controls['notification2'].setValidators([
              Validators.min(1),
              Validators.max(999),
              Validators.pattern('^-?[0-9]+$'),
            ]);
          } else {
            this.updateThresholdsForm.controls['notification1'].setValidators([
              Validators.min(1),
              Validators.max(999),
              Validators.pattern('^-?[0-9]+(.[0-9]{0,2})?$'),
            ]);
            this.updateThresholdsForm.controls['notification2'].setValidators([
              Validators.min(1),
              Validators.max(999),
              Validators.pattern('^-?[0-9]+(.[0-9]{0,2})?$'),
            ]);
          }
        }
      }),
    );
    this.addSubscription(
      combineLatest([
        this.store.select(getAlarmThresholdUpdated),
        this.store.select(getAlarmThresholdActivated),
        this.store.select(getNewAlarmThresholds),
      ]).subscribe((res: [boolean, boolean, Threshold]) => {
        const thresholdUpdated = res[0];
        const thresholdActivated = res[1];
        const newThresholds = res[2];
        if (
          thresholdUpdated !== undefined &&
          newThresholds &&
          thresholdUpdated
        ) {
          this.dispatchAlert(
            'success',
            this.translationService.getTranslatedValueFor(
              'message.confirmation.title',
            ),
            this.translationService.getTranslatedValueFor(
              'alarmThresholds.success.message',
            ),
          );
          this.option.threshold = newThresholds;
          this.reset();
          this.store.dispatch(new UpdateThresholdsReset({}));
        } else if (
          thresholdUpdated !== undefined &&
          newThresholds &&
          !thresholdUpdated
        ) {
          this.dispatchAlert(
            'error',
            this.translationService.getTranslatedValueFor(
              'message.error.title',
            ),
            this.translationService.getTranslatedValueFor(
              'alarmThresholds.error.message',
            ),
          );
          this.reset();
          this.store.dispatch(new UpdateThresholdsReset({}));
        }
        if (newThresholds) {
          this.option.threshold = {
            lowThreshold: this.roundDefaultValue(newThresholds.lowThreshold),
            highThreshold: this.roundDefaultValue(newThresholds.highThreshold),
          };
          this.reset();
          this.store.dispatch(new UpdateThresholdsReset({}));
        }
      }),
    );
  }

  public updateThresholds(): void {
    const lowThreshold = this.updateThresholdsForm.value.notification1;
    const highThreshold = this.updateThresholdsForm.value.notification2;
    const threshold =
      lowThreshold && highThreshold
        ? sortThresholdValuesFromView(
            lowThreshold || '-1',
            highThreshold || '-1',
          )
        : { lowThreshold: lowThreshold || highThreshold, highThreshold: null };
    const numberThreshold: any = {
      lowThreshold: Number(threshold.lowThreshold),
      highThreshold: threshold.highThreshold
        ? Number(threshold.highThreshold)
        : null,
    };
    this.store.dispatch(
      new UpdateThresholds({
        msisdn: this.msisdn,
        threshold: numberThreshold,
      }),
    );
  }

  public reset() {
    this.updateThresholdsForm.reset({
      notification1:
        this.option.threshold.lowThreshold === -1
          ? ''
          : this.option.threshold.lowThreshold,
      notification2:
        this.option.threshold.highThreshold === -1
          ? ''
          : this.option.threshold.highThreshold,
    });
    this.inceptionServiceInit();
  }

  private inceptionServiceInit() {
    setTimeout(() => {
      this.inceptionService.forms(this.elementRef);
      this.inceptionService.init();
    });
  }

  private dispatchAlert(
    type: 'error' | 'info' | 'success' | 'warning',
    title: string,
    description: string,
  ): void {
    const alert: Alert = {
      isClosable: true,
      alertKey: this.option.optionCodeForDescription + 'alertLevel3',
      title,
      description,
      type,
    };
    this.alertService.dispatchAlert(alert);
  }

  roundDefaultValue(value: number) {
    return this.isRes ? Number(value.toFixed(0)) : Number(value.toFixed(2));
  }
}
